/** ADCENTER — browse ANOTHER drawing's blocks and place one here.
 *
 * ⚠⚠⚠ **EVERY BLOCK IN THIS OFFICE LIVES IN A DRAWING SOMEBODY ELSE MADE.** That is the whole
 * reason DESIGNCENTER exists, and the note in `handlers.ts` has said "worth building" about it
 * since the ribbon was lifted. The hard half — carrying a definition and its nested definitions
 * between two drawings — was already built for the cross-drawing clipboard; this reaches the
 * same carry by NAME.
 *
 * ⚠ A DIALOG rather than AutoCAD's dockable tree, and the same shape `BlockDialog` uses: choose
 * the block, the dialog closes, the command line asks for the point. A block's insertion point
 * has to be a real snapped pick on the drawing — a palette that placed it at a guessed centre
 * would give a symbol that never lines up with anything.
 *
 * ⚠ The block list is sorted by USE. A block used 40 times in its own drawing is that office's
 * standard door; one used 0 times is debris somebody exploded and forgot.
 */
import { useEffect, useState } from "react";

import { loadBlocks, listDrawings, loadBlockDef,
         type BlockRow, type DrawingRow } from "./api";

export default function DesignCenter({ here, onPlace, onClose }: {
  /** The drawing being edited. ⚠ Excluded from the source list: its own blocks are INSERT's
   *  job, and offering them here would be two commands for one act. */
  here: number | null;
  /** Handed back with everything `importblock` needs. ⚠ The dialog does NOT do the work — the
   *  point is a click on the drawing, which is not something a dialog can take. */
  onPlace: (spec: { from: number; name: string; scale: number; rotation: number }) => void;
  onClose: () => void;
}) {
  const [dwgs, setDwgs] = useState<DrawingRow[]>([]);
  const [from, setFrom] = useState<number | null>(null);
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [name, setName] = useState("");
  const [scale, setScale] = useState("1");
  const [rot, setRot] = useState("0");
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState("");
  //: What the chosen block will BRING with it. ⚠ Fetched on selection rather than on place: a
  //: door that drags a title block and 400 entities in behind it is a fact a drafter should
  //: read before they commit, not discover in the layer list afterwards.
  const [brings, setBrings] = useState<{ entities: number; nested: number } | null>(null);

  useEffect(() => {
    void (async () => {
      try { setDwgs((await listDrawings()).filter((d) => d.id !== here)); }
      catch (e) { setWhy(String((e as Error)?.message || e)); }
    })();
  }, [here]);

  // ⚠⚠ **ESCAPE, WHICH EVERY OTHER DIALOG IN THIS APP HAS AND THIS ONE SHIPPED WITHOUT.** Found
  // by `qa/command_survey.py`, which types every command name in turn: it reached ADCENTER,
  // opened this, and then could not get out — 58 retries of a click the modal was intercepting.
  // A drafter would have hit the same wall with the same two keys AutoCAD trains them to use.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    setRows([]); setName(""); setBrings(null);
    if (from == null) return;
    setBusy(true);
    void (async () => {
      try { setRows(await loadBlocks(from)); }
      catch (e) { setWhy(String((e as Error)?.message || e)); }
      finally { setBusy(false); }
    })();
  }, [from]);

  useEffect(() => {
    setBrings(null);
    if (from == null || !name) return;
    void (async () => {
      try {
        const d = await loadBlockDef(from, name);
        setBrings({ entities: d.entities ?? 0, nested: d.nested ?? 0 });
      } catch { /* ⚠ silent: the count is a courtesy, and Place reports the real refusals */ }
    })();
  }, [from, name]);

  const s = Number(scale), r = Number(rot);
  const ok = from != null && !!name && Number.isFinite(s) && Math.abs(s) > 1e-9
             && Number.isFinite(r);

  const box: React.CSSProperties = {
    background: "#2b2b2b", color: "#e8e8e8", border: "1px solid #555", borderRadius: 4,
    padding: "3px 6px", font: "12px system-ui", width: "100%",
  };
  const list: React.CSSProperties = {
    background: "#222", border: "1px solid #444", borderRadius: 4, height: 260,
    overflow: "auto", font: "12px system-ui",
  };

  return (
    <div data-testid="acad-adcenter"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900,
                  display: "grid", placeItems: "center" }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#333", color: "#eee", border: "1px solid #555",
                    borderRadius: 6, padding: 16, width: 640,
                    font: "13px system-ui", boxShadow: "0 10px 40px rgba(0,0,0,.6)" }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>DesignCenter — blocks in other drawings</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ opacity: .75, marginBottom: 4 }}>Drawing</div>
            <div style={list} data-testid="acad-adc-dwgs">
              {dwgs.map((d) => (
                <div key={d.id} onClick={() => setFrom(d.id)}
                     style={{ padding: "4px 8px", cursor: "pointer",
                              background: from === d.id ? "#0a5" : "transparent" }}>
                  {d.name}
                </div>))}
              {!dwgs.length && <div style={{ padding: 8, opacity: .6 }}>no other drawings</div>}
            </div>
          </div>
          <div>
            <div style={{ opacity: .75, marginBottom: 4 }}>
              Block {rows.length ? `· ${rows.length}` : ""}
            </div>
            <div style={list} data-testid="acad-adc-blocks">
              {busy && <div style={{ padding: 8, opacity: .6 }}>reading…</div>}
              {!busy && rows.map((b) => (
                <div key={b.name} onClick={() => setName(b.name)}
                     style={{ padding: "4px 8px", cursor: "pointer", display: "flex",
                              justifyContent: "space-between",
                              background: name === b.name ? "#0a5" : "transparent" }}>
                  <span>{b.name}</span>
                  {/* ⚠ The usage count IS the sort key and the only signal of which of 300
                      blocks is the one anybody uses. */}
                  <span style={{ opacity: .6 }}>{b.used}×</span>
                </div>))}
              {!busy && from != null && !rows.length
                && <div style={{ padding: 8, opacity: .6 }}>that drawing has no named blocks</div>}
              {from == null && <div style={{ padding: 8, opacity: .6 }}>pick a drawing first</div>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <label style={{ flex: "0 0 auto" }}>Scale{" "}
            <input value={scale} onChange={(e) => setScale(e.target.value)}
                   data-testid="acad-adc-scale" style={{ ...box, width: 70 }} /></label>
          <label style={{ flex: "0 0 auto" }}>Rotation{" "}
            <input value={rot} onChange={(e) => setRot(e.target.value)}
                   data-testid="acad-adc-rot" style={{ ...box, width: 70 }} /></label>
          <div style={{ flex: 1, opacity: .7, fontSize: 12 }}>
            {brings
              ? `brings ${brings.entities} object${brings.entities === 1 ? "" : "s"}`
                + (brings.nested ? ` and ${brings.nested} nested block`
                                   + `${brings.nested === 1 ? "" : "s"}` : "")
              : ""}
          </div>
        </div>
        {why && <div style={{ color: "#e88", marginTop: 8, fontSize: 12 }}>{why}</div>}
        {/* ⚠ SAID IN THE DIALOG, not discovered afterwards: a block already here is KEPT, because
            redefining one would reshape every reference already on the plan. */}
        <div style={{ opacity: .6, fontSize: 11, marginTop: 8 }}>
          A block this drawing already has is kept as it is — placing one from elsewhere never
          redefines it.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button onClick={onClose}
                  style={{ ...box, width: "auto", cursor: "pointer" }}>Cancel</button>
          <button data-testid="acad-adc-place" disabled={!ok}
                  onClick={() => ok && onPlace({ from: from!, name, scale: s, rotation: r })}
                  style={{ ...box, width: "auto", cursor: ok ? "pointer" : "default",
                           background: ok ? "#0a5" : "#3a3a3a" }}>
            Place…
          </button>
        </div>
      </div>
    </div>);
}
